/**
 * pluginRegistry.ts - FMwvTool Plugin Registry
 * Central registry of all available plugins
 * 
 * CHANGE HISTORY:
 * - 251107_2235 Initial creation - Pluggr integration
 * 
 * PLUGINS:
 * - document-manager: PDF viewing, Office conversion (migrated from fmwvtool-v4)
 * - data-processor: CSV, vCard, XML parsing (planned)
 * - advanced-ui: Data grids, forms, panels (planned)
 */

export interface PluginRegistryEntry {
  id: string;
  name: string;
  description: string;
  version: string;
  path: string;
  enabled: boolean;
  source: 'local' | 'url' | 'npm' | 'registry' | 'pluggr';
  category: string;
  tags: string[];
  author: string;
  showInNav?: boolean; // If false, plugin is hidden from navigation (default: true)
  migrated?: boolean;
  migratedFrom?: string;
  buildDate?: string; // ISO 8601 datetime when plugin was last built/updated
}

/**
 * Current build date for new/updated plugins
 * Format: YYYY-MM-DDTHH:MM:SSZ (ISO 8601)
 */
const BUILD_DATE = new Date().toISOString();

/**
 * FMwvTool Plugin Registry
 * All available plugins for the application
 */
export const pluginRegistry: PluginRegistryEntry[] = [
  {
    id: 'package-test',
    name: 'Package Tests',
    description: 'Test packages directly without dependr to verify isolated functionality',
    version: '1.0.0',
    path: './plugins/PackageTestPlugin',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['testing', 'packages', 'debug', 'development'],
    author: 'OpCentral',
    buildDate: BUILD_DATE
  },
  {
    id: 'picker',
    name: 'Picker Menu',
    description: 'Keyboard-navigable picker menu for FileMaker integration',
    version: '1.0.0',
    path: './plugins/PickerPlugin',
    enabled: true,
    source: 'local',
    category: 'ui',
    tags: ['filemaker', 'picker', 'menu', 'keyboard', 'selection'],
    author: 'OpCentral'
  },
  {
    id: 'field-matcher',
    name: 'Field Matcher + Data API Generator',
    description: 'Match source fields to target fields and generate Data API JSON',
    version: '1.0.0',
    path: './plugins/FieldMatcherPlugin',
    enabled: true,
    source: 'local',
    category: 'data',
    tags: ['filemaker', 'data-api', 'field-mapping', 'integration'],
    author: 'OpCentral'
  },
  {
    id: 'file-loader',
    name: 'File Loader',
    description: 'Drag & drop file loading with preview and validation',
    version: '1.0.0',
    path: './plugins/file-loader',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['files', 'upload', 'drag-drop'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fm-wv-utility'
  },
  {
    id: 'selectr-examples',
    name: 'Selectr Examples',
    description: 'Interactive examples of Selectr components and patterns',
    version: '1.0.0',
    path: './plugins/selectr-examples',
    enabled: true,
    source: 'local',
    category: 'examples',
    tags: ['selectr', 'examples', 'ui', 'pickers'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fm-wv-utility'
  },
  {
    id: 'selectr-demo',
    name: 'Selectr Display Options',
    description: 'Interactive demo for testing Selectr display modes and options',
    version: '1.0.0',
    path: './tools/SelectrDemo',
    enabled: true,
    source: 'local',
    category: 'examples',
    tags: ['selectr', 'demo', 'ui', 'configuration'],
    author: 'OpCentral',
    buildDate: BUILD_DATE
  },
  {
    id: 'list-manager',
    name: 'List Manager',
    description: 'Manage OpCentral list library with Outlinr and Organizr',
    version: '2.0.0',
    path: 'list-manager', // Loaded via PluginLoader from @opcentral/pluggr
    enabled: true,
    source: 'pluggr',
    category: 'data',
    tags: ['lists', 'organizr', 'outlinr', 'listr', 'data-management', 'json'],
    author: 'OpCentral',
    buildDate: BUILD_DATE,
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'pdf-viewer',
    name: 'PDF Viewer',
    description: 'View PDF files with text extraction and navigation',
    version: '1.0.0',
    path: './plugins/pdf-viewer',
    enabled: true,
    source: 'local',
    category: 'document',
    tags: ['pdf', 'documents', 'viewer', 'extraction'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'office-viewer',
    name: 'Office Viewer',
    description: 'View and convert Office documents (DOCX, XLSX, PPTX)',
    version: '1.0.0',
    path: './plugins/office-viewer',
    enabled: true,
    source: 'local',
    category: 'document',
    tags: ['office', 'docx', 'xlsx', 'documents'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'transform-distill',
    name: 'Data Transformer',
    description: 'Transform and distill data with filters and operations',
    version: '1.0.0',
    path: './plugins/data-transformer',
    enabled: true,
    source: 'local',
    category: 'data',
    tags: ['data', 'transform', 'filter'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'table-viewer',
    name: 'Table Viewer',
    description: 'FileMaker portal-style table with GTRR, script triggers, and action buttons',
    version: '2.0.0',
    path: './plugins/TableViewerPlugin',
    enabled: true,
    source: 'local',
    category: 'data',
    tags: ['table', 'data', 'grid', 'filemaker', 'portal', 'gtrr'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4',
    buildDate: BUILD_DATE
  },
  {
    id: 'tag-manager',
    name: 'Tag Manager',
    version: '2.0.0',
    description: 'Multi-interface tag management plugin with 5 modes: compact, full, inline, modal, cloud',
    path: './plugins/TagManagerPlugin',
    enabled: true,
    source: 'local',
    category: 'data',
    tags: ['tags', 'taggr', 'autocomplete', 'filemaker', 'organization', 'multi-interface'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'tool',
    buildDate: BUILD_DATE
  },
  {
    id: 'filemaker-bridge',
    name: 'FileMaker Bridge',
    description: 'Bridge between FileMaker and JavaScript',
    version: '1.0.0',
    path: './plugins/filemaker-bridge',
    enabled: true,
    source: 'local',
    category: 'integration',
    tags: ['filemaker', 'bridge', 'integration'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'fm-printable-to-xml',
    name: 'FM Printable → XML',
    description: 'Convert FileMaker printable script steps to XML format',
    version: '1.0.0',
    path: 'filemaker/FileMakerPrintableToXML', // Loaded via PluginLoader from @opcentral/pluggr
    enabled: true,
    source: 'pluggr', // From Pluggr package
    category: 'filemaker-scripts',
    tags: ['filemaker', 'scripts', 'xml', 'conversion', 'utility'],
    author: 'OpCentral',
    showInNav: false, // Utility plugin - access via command palette
    buildDate: BUILD_DATE
  },
  {
    id: 'fm-xml-to-clipboard',
    name: 'FM XML → Clipboard',
    description: 'Convert FileMaker XML to clipboard-ready script steps',
    version: '1.0.0',
    path: 'filemaker/FileMakerXMLToClipboard', // Loaded via PluginLoader from @opcentral/pluggr
    enabled: true,
    source: 'pluggr', // From Pluggr package
    category: 'filemaker-scripts',
    tags: ['filemaker', 'scripts', 'xml', 'clipboard', 'utility'],
    author: 'OpCentral',
    showInNav: false, // Utility plugin - access via command palette
    buildDate: BUILD_DATE
  },
  {
    id: 'fmwvtool-script-converter',
    name: 'FMwvTool Script Converter',
    description: 'Convert FMwvTool scripts between formats and generate FileMaker integration code',
    version: '1.0.0',
    path: 'filemaker/FMwvToolScriptConverter', // Loaded via PluginLoader from @opcentral/pluggr
    enabled: true,
    source: 'pluggr', // From Pluggr package
    category: 'filemaker-scripts',
    tags: ['filemaker', 'scripts', 'conversion', 'fmwvtool', 'utility'],
    author: 'OpCentral',
    showInNav: false, // Utility plugin - access via command palette
    buildDate: BUILD_DATE
  },
  {
    id: 'rmemb',
    name: 'Rmemb',
    description: 'Remember and recall data and context',
    version: '1.0.0',
    path: './plugins/rmemb',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['memory', 'notes', 'recall'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'setup-wizard',
    name: 'Setup Wizard',
    description: 'Guided setup and configuration',
    version: '1.0.0',
    path: './plugins/setup-wizard',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['setup', 'wizard', 'configuration'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'documentation',
    name: 'Documentation',
    description: 'Interactive documentation and help',
    version: '1.0.0',
    path: './plugins/documentation',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['docs', 'help', 'guide'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'quick-reference',
    name: 'Quick Reference',
    description: 'Quick reference for shortcuts and tasks',
    version: '1.0.0',
    path: './plugins/quick-reference',
    enabled: true,
    source: 'local',
    category: 'utility',
    tags: ['reference', 'shortcuts', 'help'],
    author: 'OpCentral',
    migrated: true,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'outliner',
    name: 'Outliner',
    description: 'Hierarchical data outliner with FileMaker integration via Field Matcher',
    version: '1.0.0',
    path: './plugins/outliner',
    enabled: true,
    source: 'local',
    category: 'data',
    tags: ['outliner', 'hierarchy', 'filemaker', 'field-matcher', 'data-structure', 'tree'],
    author: 'OpCentral',
    buildDate: BUILD_DATE
  },
  {
    id: 'document-manager',
    name: 'Document Manager',
    description: 'PDF viewing, Office document conversion (DOCX, XLSX), and file handling',
    version: '1.0.0',
    path: './plugins/document-manager/plugin',
    enabled: false, // Not yet converted
    source: 'local',
    category: 'document-processing',
    tags: ['pdf', 'office', 'documents', 'conversion'],
    author: 'OpCentral',
    migrated: false,
    migratedFrom: 'fmwvtool-v4'
  },
  // Future plugins
  {
    id: 'data-processor',
    name: 'Data Processor',
    description: 'CSV, vCard, XML parsing and data transformation',
    version: '1.0.0',
    path: './plugins/data-processor/plugin',
    enabled: false, // Not yet implemented
    source: 'local',
    category: 'data-processing',
    tags: ['csv', 'vcard', 'xml', 'parsing'],
    author: 'OpCentral',
    migrated: false,
    migratedFrom: 'fmwvtool-v4'
  },
  {
    id: 'advanced-ui',
    name: 'Advanced UI',
    description: 'Data grids, form builder, and panel management',
    version: '1.0.0',
    path: './plugins/advanced-ui/plugin',
    enabled: false, // Not yet implemented
    source: 'local',
    category: 'ui-components',
    tags: ['grid', 'forms', 'panels', 'ui'],
    author: 'OpCentral',
    migrated: false,
    migratedFrom: 'fmwvtool-v4'
  }
];

/**
 * Get enabled plugins
 */
export function getEnabledPlugins(): PluginRegistryEntry[] {
  return pluginRegistry.filter(p => p.enabled);
}

/**
 * Get plugin by ID
 */
export function getPlugin(id: string): PluginRegistryEntry | undefined {
  return pluginRegistry.find(p => p.id === id);
}

/**
 * Get plugins by category
 */
export function getPluginsByCategory(category: string): PluginRegistryEntry[] {
  return pluginRegistry.filter(p => p.category === category);
}

/**
 * Get plugins by tag
 */
export function getPluginsByTag(tag: string): PluginRegistryEntry[] {
  return pluginRegistry.filter(p => p.tags.includes(tag));
}

/**
 * Get migrated plugins
 */
export function getMigratedPlugins(): PluginRegistryEntry[] {
  return pluginRegistry.filter(p => p.migrated);
}

/**
 * Plugin statistics
 */
export function getPluginStats() {
  return {
    total: pluginRegistry.length,
    enabled: pluginRegistry.filter(p => p.enabled).length,
    disabled: pluginRegistry.filter(p => !p.enabled).length,
    migrated: pluginRegistry.filter(p => p.migrated).length,
    categories: [...new Set(pluginRegistry.map(p => p.category))],
    tags: [...new Set(pluginRegistry.flatMap(p => p.tags))]
  };
}

// Add Workflow Manager Plugin (replaces Runner + Inspector)
pluginRegistry.push({
  id: 'workflow-manager',
  name: 'Workflow Manager',
  description: 'Comprehensive workflow management: inspect, edit, run, debug, and version control',
  version: '2.0.0',
  path: './plugins/workflow-manager/WorkflowManagerPlugin',
  enabled: true,
  source: 'local',
  category: 'automation',
  tags: ['workflow', 'orchestration', 'automation', 'inspector', 'editor', 'debug', 'test', 'filemaker'],
  author: 'OpCentral',
  showInNav: true,
  buildDate: BUILD_DATE
});

// Add Script Converter Plugin
pluginRegistry.push({
  id: 'script-converter',
  name: 'Script Converter',
  description: 'Convert FileMaker printable scripts to XML for pasting into Script Workspace',
  version: '1.0.0',
  path: './plugins/script-converter/ScriptConverterPlugin',
  enabled: true,
  source: 'local',
  category: 'filemaker-scripts',
  tags: ['filemaker', 'scripts', 'xml', 'conversion', 'utility'],
  author: 'OpCentral',
  showInNav: true,
  buildDate: BUILD_DATE
});
